import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertTriangle, TrendingDown, HelpCircle, Clock, Users, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { apiFetch } from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-Analytics-Dashboard: Drop-off, Verweildauer, Quiz-Erfolgsquote.
 * Lädt aggregiert von /api/admin/analytics/drop-offs.
 */
export const AdminAnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/admin/analytics/drop-offs?days=${d}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-line */ }, [days]);

  const s = data?.summary || {};
  const lessons = data?.top_drop_off_lessons || [];
  const questions = data?.quiz_success_by_question || [];
  const abandons = data?.quiz_abandons || [];

  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      {/* Zeitraum-Filter */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
        <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-white text-xl">📊 Analytics &amp; Drop-offs</CardTitle>
            <p className="text-gray-400 text-sm mt-1">Wo brechen Mitarbeiter ab, wo hakt's im Quiz?</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="bg-gray-900 border border-gray-700 text-gray-200 text-sm rounded-md px-2 py-1"
              data-testid="analytics-days-filter"
            >
              <option value={7}>7 Tage</option>
              <option value={30}>30 Tage</option>
              <option value={90}>90 Tage</option>
            </select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => load(days)}
              disabled={loading}
              className="border-yellow-500/40 text-yellow-300"
              data-testid="analytics-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* KPI-Kacheln */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiTile icon={<Users className="w-4 h-4" />} label="Lesson-Starts" value={s.total_starts ?? 0} color="text-blue-300" data-testid="kpi-starts" />
            <KpiTile icon={<TrendingDown className="w-4 h-4" />} label="Abschlüsse" value={s.total_completes ?? 0} color="text-green-300" data-testid="kpi-completes" />
            <KpiTile icon={<Clock className="w-4 h-4" />} label="Abschluss-Quote" value={`${s.overall_completion_rate_pct ?? 0}%`} color="text-yellow-300" data-testid="kpi-completion-rate" />
            <KpiTile icon={<AlertTriangle className="w-4 h-4" />} label="Events (gesamt)" value={s.total_events ?? 0} color="text-gray-300" data-testid="kpi-events" />
          </div>
        </CardContent>
      </Card>

      {/* Top-Abbrecher-Lektionen */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-red-500/20">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-400" />
            Top-Abbrecher-Lektionen
          </CardTitle>
          <p className="text-gray-500 text-xs">Sortiert nach Anzahl Exit-Events. Wo müssen wir Content verbessern?</p>
        </CardHeader>
        <CardContent>
          {lessons.length === 0 ? (
            <p className="text-gray-500 text-sm italic">Noch keine Daten – warte bis Mitarbeiter Lektionen starten.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drop-off-table">
                <thead>
                  <tr className="text-gray-500 text-xs uppercase tracking-wider border-b border-gray-800">
                    <th className="text-left py-2 px-2">Lektion</th>
                    <th className="text-right py-2 px-2">Starts</th>
                    <th className="text-right py-2 px-2">Abschlüsse</th>
                    <th className="text-right py-2 px-2">Drop-off</th>
                    <th className="text-right py-2 px-2">Ø Exit %</th>
                    <th className="text-right py-2 px-2">Ø Zeit</th>
                  </tr>
                </thead>
                <tbody>
                  {lessons.slice(0, 15).map((l, i) => (
                    <tr key={i} className="border-b border-gray-900 hover:bg-gray-900/40" data-testid={`drop-off-row-${i}`}>
                      <td className="py-2 px-2">
                        <p className="text-white font-medium truncate max-w-xs">{l.title}</p>
                        <p className="text-gray-500 text-[10px]">{l.module_id} · {l.lesson_type || 'content'}</p>
                      </td>
                      <td className="text-right py-2 px-2 text-blue-300 font-mono">{l.starts}</td>
                      <td className="text-right py-2 px-2 text-green-300 font-mono">{l.completes}</td>
                      <td className={`text-right py-2 px-2 font-mono font-bold ${(l.drop_off_rate_pct ?? 0) > 50 ? 'text-red-400' : (l.drop_off_rate_pct ?? 0) > 25 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {l.drop_off_rate_pct ?? '–'}%
                      </td>
                      <td className="text-right py-2 px-2 text-gray-300 font-mono">{l.avg_exit_progress_pct}%</td>
                      <td className="text-right py-2 px-2 text-gray-400 font-mono">
                        {l.avg_time_spent_sec > 60 ? `${Math.round(l.avg_time_spent_sec / 60)} min` : `${Math.round(l.avg_time_spent_sec)}s`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quiz-Erfolgsquote pro Frage */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-500/20">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-yellow-400" />
            Quiz-Erfolgsquote pro Frage
          </CardTitle>
          <p className="text-gray-500 text-xs">Fragen mit niedriger Erfolgsquote sind zu schwer oder unklar formuliert.</p>
        </CardHeader>
        <CardContent>
          {questions.length === 0 ? (
            <p className="text-gray-500 text-sm italic">Noch keine Quiz-Daten.</p>
          ) : (
            <div className="space-y-2" data-testid="quiz-success-list">
              {questions.slice(0, 20).map((q, i) => {
                const rate = q.success_rate_pct;
                const color = rate < 50 ? 'bg-red-500' : rate < 75 ? 'bg-yellow-500' : 'bg-green-500';
                return (
                  <div key={i} className="rounded-lg bg-black/30 border border-gray-800 p-3" data-testid={`quiz-question-row-${i}`}>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <p className="text-white text-xs font-mono truncate flex-1">{q.question_id}</p>
                      <p className={`text-xs font-black ${rate < 50 ? 'text-red-400' : rate < 75 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {rate}% ({q.correct_count}/{q.attempts})
                      </p>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-800 overflow-hidden">
                      <div className={`h-full ${color}`} style={{ width: `${rate}%` }} />
                    </div>
                    <p className="text-gray-500 text-[10px] mt-1">{q.module_id} · {q.lesson_id}</p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quiz-Abbrüche */}
      {abandons.length > 0 && (
        <Card className="bg-gradient-to-br from-gray-900 to-black border-orange-500/20">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Quiz-Abbrüche pro Frage
            </CardTitle>
            <p className="text-gray-500 text-xs">Letzte gesehene Frage bevor Nutzer das Quiz verlassen haben (ohne Submit).</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5" data-testid="quiz-abandons-list">
              {abandons.slice(0, 10).map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm py-1 border-b border-gray-900 last:border-0">
                  <div className="min-w-0">
                    <p className="text-white text-xs font-mono truncate">{a.last_question_id || '–'}</p>
                    <p className="text-gray-500 text-[10px]">{a.module_id} · {a.lesson_id}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-orange-300 font-bold text-sm">{a.abandons}× abgebrochen</p>
                    <p className="text-gray-500 text-[10px]">Ø {a.avg_answered} Antworten davor</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const KpiTile = ({ icon, label, value, color, ...rest }) => (
  <div className="rounded-lg bg-black/40 border border-gray-800 px-3 py-3" {...rest}>
    <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wider ${color} font-bold`}>
      {icon}
      <span>{label}</span>
    </div>
    <p className="text-white font-black text-2xl mt-1">{value}</p>
  </div>
);

export default AdminAnalyticsPanel;
